import { useMemo } from "react";

import {
  getErrorMessages,
  useGetCategoriesQuery,
  useGetSavingQuery,
} from "../../../../services";
import { WindowType } from "../../../../utils";
import { FILTER_OPTIONS } from "../../../../utils/constants";
import { localToday, toIsoDate } from "../../../../utils/dates";
import { SavingDetails, UpdateSavingsObject } from "../../types";
import {
  balanceMovingRowsOf,
  dayOf,
  isHideableWindow,
  visibleWindowOptions,
} from "../utils";
import { UseSavingDataProps, UseSavingDataReturn } from "./types";

export const useSavingData = ({
  id,
  window,
}: UseSavingDataProps): UseSavingDataReturn => {
  const {
    currentData: lifetimeData,
    error: lifetimeError,
    isLoading: isLifetimeLoading,
    isFetching: isLifetimeFetching,
    refetch: refetchLifetime,
  } = useGetSavingQuery({ id, window: WindowType.ALL });

  const firstMovement = lifetimeData
    ? balanceMovingRowsOf(lifetimeData)[0]
    : undefined;
  const windowOptions = useMemo(
    () =>
      visibleWindowOptions(
        FILTER_OPTIONS,
        firstMovement && dayOf(firstMovement.date),
        toIsoDate(localToday()),
      ),
    [firstMovement?.date],
  );
  const isWindowOffered = windowOptions.some(({ value }) => value === window);
  // Until lifetime resolves a 3Y/5Y may turn out hidden, so it reads ALL — the lifetime query's own cache entry.
  const isWindowSettled = Boolean(lifetimeData) || !isHideableWindow(window);
  // The persisted choice is shared by every saving, so a hidden one falls back here without being overwritten.
  const effectiveWindow =
    isWindowOffered && isWindowSettled ? window : WindowType.ALL;

  const {
    data,
    error: queryError,
    isLoading,
    isFetching,
    refetch,
  } = useGetSavingQuery({ id, window: effectiveWindow });
  const {
    data: incomeCategories,
    isLoading: isLoadingIncomeCategories,
    isError: hasIncomeCategoriesError,
  } = useGetCategoriesQuery(true);
  const { data: categoriesExpense = [] } = useGetCategoriesQuery(false);

  const saving = data ?? ({} as SavingDetails);
  const lifetimeSaving = lifetimeData ?? ({} as SavingDetails);
  const savingUpdate: UpdateSavingsObject = data
    ? {
        id: data.id,
        cashbackMode: data.cashbackMode,
        cashbackRate: data.cashbackRate,
        cashbackMonthlyCap: data.cashbackMonthlyCap,
        name: data.name,
        institution: data.institution,
        startDate: data.startDate ? new Date(data.startDate) : undefined,
      }
    : ({} as UpdateSavingsObject);

  const lastUpdate = balanceMovingRowsOf(saving).at(-1);
  let lastUpdated: Date | null;
  if (lastUpdate) {
    lastUpdated = new Date(lastUpdate.date);
  } else if (data) {
    lastUpdated = new Date();
  } else {
    lastUpdated = null;
  }

  const activeError = queryError ?? lifetimeError;
  const errorMessages = activeError ? getErrorMessages(activeError as any) : [];
  const error = errorMessages[0] ?? null;

  return {
    window: effectiveWindow,
    windowOptions,
    saving,
    lifetimeSaving,
    savingUpdate,
    categoriesIncome: incomeCategories ?? [],
    categoriesExpense,
    isLoadingIncomeCategories,
    hasIncomeCategoriesError,
    isLoading:
      isLoading ||
      (isFetching && !data) ||
      isLifetimeLoading ||
      (isLifetimeFetching && !lifetimeData),
    error,
    notFoundError: error === "Saving not found",
    lastUpdated,
    refetch: async () => {
      await Promise.all([refetch(), refetchLifetime()]);
    },
  };
};
